<script setup>
import { useAppStore } from '~/store/main'
import noticeCategoryBySlug from '~/assets/gql/noticeCategoryBySlug.gql'

const store = useAppStore()
const { getMenuConfig } = storeToRefs(store)
const { setRootMenu, setRoot, setBack } = store

const notice = ref('')

const { t, locale } = useI18n()
const localePath = useLocalePath()
const route = useRoute()

const variables = computed(() => ({
  language: locale.value.toUpperCase(),
  slug: route.params.slug,
}))

const { result } = useQuery(noticeCategoryBySlug, variables, {
  enabled: computed(
    () => route.name?.includes('notice-slug') && !!route.params.slug
  ),
})

watch(
  () => result.value,
  (newData) => {
    if (route.name?.includes('notice-slug')) {
      if (newData?.notices?.nodes[0]?.categories?.edges[0]?.node) {
        notice.value = newData.notices.nodes[0].categories.edges[0].node
      }
    }
  },
  { immediate: true }
)

const breadCrumber = computed(() => {
  const slugArr = route.path.split('/')
  let breadcrumber = ['home']
  let skip = false
  let root = ''

  if (route.path === '/') {
    setRoot('')
    // setRootMenu("");
    setBack({
      label: '',
      slug: '',
      contentType: '',
      root: '',
      id: '',
      name: 'index',
    })
    return breadcrumber
  }

  if (route.name?.includes('search')) {
    breadcrumber[1] = {
      label: t('general.search'),
      slug: '#search',
      contentType: '',
      id: '',
      root: '',
    }
    setRoot('')
    setRootMenu('')
    setBack({
      label: '',
      slug: '',
      contentType: '',
      root: '',
      id: '',
      name: 'index',
    })
    return breadcrumber
  }

  if (route.name?.includes('other-people')) {
    root = 'akademie'
    breadcrumber[1] = root
    breadcrumber[2] = {
      label: t('people.people'),
      root,
      slug: '#lide',
      contentType: '',
      id: '',
    }
    setRoot(root)
    setBack({
      label: '',
      slug: '',
      contentType: '',
      root: '',
      id: '',
      name: 'other-people',
    })
    return breadcrumber
  }

  if (route.name?.includes('student-slug')) {
    root = 'akademie'
    breadcrumber[1] = root
    breadcrumber[2] = {
      label: t('people.people'),
      root,
      slug: '#lide',
      contentType: '',
      id: '',
    }
    breadcrumber[3] = {
      label:
        locale.value === 'cs'
          ? slugArr[2].charAt(0).toUpperCase() + slugArr[2].slice(1)
          : slugArr[3].charAt(0).toUpperCase() + slugArr[3].slice(1),
      slug: locale.value === 'cs' ? slugArr[2] : slugArr[3],
      contentType: 'student',
      id: '',
      root: '',
    }
    setRoot(root)
    setBack({
      label: '',
      slug: '',
      contentType: '',
      root: '',
      id: '',
      name: 'other-people',
    })
    return breadcrumber
  }

  if (route.name?.includes('employee-slug')) {
    root = 'akademie'
    breadcrumber[1] = root
    breadcrumber[2] = {
      label: t('people.people'),
      root,
      slug: '#lide',
      contentType: '',
      id: '',
    }
    breadcrumber[3] = {
      label:
        locale.value === 'cs'
          ? slugArr[2].charAt(0).toUpperCase() + slugArr[2].slice(1)
          : slugArr[3].charAt(0).toUpperCase() + slugArr[3].slice(1),
      slug: locale.value === 'cs' ? slugArr[2] : slugArr[3],
      contentType: 'employee',
      id: '',
      root: '',
    }
    setRoot(root)
    setBack({
      label: '',
      slug: '',
      contentType: '',
      root: '',
      id: '',
      name: 'other-people',
    })
    return breadcrumber
  }

  if (route.name?.includes('other-publication')) {
    root = 'akademie'
    breadcrumber[1] = root
    breadcrumber[2] = {
      label: t('publications.publications'),
      root,
      slug: '#publikace',
      contentType: '',
      id: '',
    }
    setRoot(root)
    setBack({
      label: '',
      slug: '',
      contentType: '',
      root: '',
      id: '',
      name: 'other-publication',
    })
    return breadcrumber
  }

  if (route.name?.includes('publication-slug')) {
    root = 'akademie'
    breadcrumber[1] = root
    breadcrumber[2] = {
      label: t('publications.publications'),
      root,
      slug: '#publikace',
      contentType: '',
      id: '',
    }
    const slug = locale.value === 'cs' ? slugArr[2] : slugArr[3]
    let name = slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, ' ')
    if (name.length > 10) name = name.slice(0, 10) + '...'
    breadcrumber[3] = {
      label: name,
      slug,
      contentType: 'publication',
      id: '',
      root: '',
    }
    setRoot(root)
    setBack({
      label: '',
      slug: '',
      contentType: '',
      root: '',
      id: '',
      name: 'other-publication',
    })
    return breadcrumber
  }

  if (route.name?.includes('program-category')) {
    root = 'program'
    breadcrumber[1] = root
    const slug = locale.value === 'cs' ? slugArr[2] : slugArr[3]
    let name = slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, ' ')
    if (name.length > 20) name = name.slice(0, 20) + '...'
    breadcrumber[2] = {
      label: name,
      slug,
      contentType: 'category',
      id: '',
      root: '',
    }
    setRoot(root)
    setBack({
      label: '',
      slug: '',
      contentType: '',
      root: '',
      id: '',
      name: 'program',
    })
    return breadcrumber
  }

  if (route.name?.includes('notice-slug')) {
    root = 'program'
    breadcrumber[1] = root
    const slug = locale.value === 'cs' ? slugArr[2] : slugArr[3]
    let name = slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, ' ')
    if (name.length > 20) name = name.slice(0, 20) + '...'
    if (notice.value?.name) {
      breadcrumber[2] = {
        label: notice.value.name,
        slug: notice.value.slug,
        contentType: 'category',
        id: '',
        root: '',
      }
      breadcrumber[3] = {
        label: name,
        slug,
        contentType: 'notice',
        id: '',
        root: '',
      }
    } else {
      breadcrumber[2] = {
        label: name,
        slug,
        contentType: 'notice',
        id: '',
        root: '',
      }
    }
    setRoot(root)
    setBack({
      label: '',
      slug: '',
      contentType: '',
      root: '',
      id: '',
      name: 'program',
    })
    return breadcrumber
  }

  getMenuConfig.value?.forEach((firstLevel) => {
    if (skip) return

    if (
      firstLevel.node.label === 'Studium' ||
      firstLevel.node.label === 'Study'
    ) {
      root = 'studium'
    } else if (
      firstLevel.node.label === 'Akademie' ||
      firstLevel.node.label === 'Academy'
    ) {
      root = 'akademie'
    } else if (firstLevel.node.label === 'Program') {
      root = 'program'
    }
    breadcrumber[1] = root

    if (
      (route.name?.includes('studium__') && root === 'studium') ||
      (route.name?.includes('akademie__') && root === 'akademie') ||
      (route.name?.includes('program__') && root === 'program')
    ) {
      if (breadcrumber.length > 2) breadcrumber = breadcrumber.slice(0, 2)
      skip = true
    }

    firstLevel?.node?.childItems?.nodes.forEach((secondLevel) => {
      if (skip) return
      breadcrumber[2] = {
        label: secondLevel?.label,
        root,
        slug: secondLevel?.connectedNode?.node?.slug || secondLevel.path,
        contentType:
          secondLevel?.connectedNode?.node?.contentType?.node?.name ||
          secondLevel?.connectedNode?.node?.taxonomy?.node?.name,
        id: secondLevel?.id,
      }

      if (
        route.path === secondLevel?.path ||
        (locale.value === 'cs' && slugArr[2] === secondLevel?.id) ||
        (locale.value === 'en' && slugArr[3] === secondLevel?.id)
      ) {
        if (breadcrumber.length > 3) breadcrumber.pop()
        skip = true
      }

      secondLevel?.childItems?.nodes.forEach((thirdLevel) => {
        if (skip) return
        breadcrumber[3] = {
          label: thirdLevel?.label,
          root,
          slug: thirdLevel?.connectedNode?.node?.slug || '#',
          contentType: thirdLevel?.connectedNode?.node?.contentType?.node?.name,
          id: thirdLevel?.id,
        }
        if (route.path === thirdLevel?.path) skip = true
      })
    })
  })

  if (!skip) {
    breadcrumber = ['home']
    setRoot('')
    setBack({
      label: '',
      slug: '',
      contentType: '',
      root: '',
      id: '',
      name: 'index',
    })
    return breadcrumber
  }

  setRoot(root)
  const back = breadcrumber[breadcrumber.length - 2]
  if (typeof back === 'string') {
    setBack({
      label: '',
      slug: '',
      contentType: '',
      root: '',
      id: '',
      name: back,
    })
  } else {
    setBack(back)
  }
  return breadcrumber
})
</script>

<template>
  <div v-if="route.name" class="breadcrumber">
    <div
      v-for="(item, index) in breadCrumber"
      :key="index"
      class="breadcrumber__crumb text_minor_2 is_c6"
    >
      <span v-if="index !== 0"> → &nbsp; </span>

      <div v-if="item === 'home'">
        <NuxtLink class="link__inverse" :to="localePath('index')">
          ♥ {{ t('general.home') }}
        </NuxtLink>
      </div>

      <div v-else-if="item === 'studium'">
        <NuxtLink class="link__inverse" :to="localePath('studium')">
          {{ t('general.study') }}
        </NuxtLink>
      </div>

      <div v-else-if="item === 'akademie'">
        <NuxtLink class="link__inverse" :to="localePath('akademie')">
          {{ t('general.academy') }}
        </NuxtLink>
      </div>

      <div v-else-if="item === 'program'">
        <NuxtLink class="link__inverse" :to="localePath('program')">
          {{ t('general.program') }}
        </NuxtLink>
      </div>

      <div v-else>
        <RowLink
          :id="item.id"
          class="text_minor_2"
          :label="item.label"
          :slug="item.slug"
          :content-type="item.contentType"
          :root="item.root"
          :bread="true"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.breadcrumber {
  display: flex;
  flex-flow: row wrap;

  &__crumb {
    display: flex;
    flex-flow: row nowrap;
    margin: 3px;
  }
}
</style>
